import PropTypes from "prop-types";
import { useComponentVisible } from "../../../hooks";
import { useFilter } from "../hooks/useFilter";
import { titleCase } from "@lib/formatString";
import { CheckIcon } from "@components/Icon";
import { COLOUR_TAGS, SIZE, TYPE, STANDARD_SIZES, DRESS_SIZES, PANT_SIZES } from "../constants";
import styles from "./styles/FilterListMobile.module.css";

export const FilterListMobile = ({
  filterName,
  filterLabels,
  filteredValues,
  onChange,
  setSelectedFilter,
  setShowFilterMenu,
}) => {
  // Hook that alerts clicks outside of the passed ref
  const { ref } = useComponentVisible(() => setSelectedFilter(""));
  const { handleChange, clearValues, updatedValues } = useFilter({
    filterName,
    filterLabels,
    filteredValues,
    onChange,
  });

  const back = () => {
    setSelectedFilter("");
    setShowFilterMenu(true);
  };

  const title = (key) => {
    if (key === COLOUR_TAGS) return "Colours";
    return `${titleCase(key)}s`;
  };

  const trimType = (key) => {
    const isolatedType = key.replace("type:", "").replace("type-fr:", "").trim();
    return titleCase(isolatedType);
  };

  const sizeLists = { standard: [], dress: [], pants: [] };

  // creates different list sections for sizing
  STANDARD_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.standard.push(size);
    }
  });

  DRESS_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.dress.push(size);
    }
  });

  PANT_SIZES.forEach((size) => {
    if (filterLabels.includes(size.toLowerCase())) {
      sizeLists.pants.push(size);
    }
  });

  const sectionExists = (value) =>
    value.toLowerCase() !== "default" && value.toLowerCase() !== "n/a";

  const Header = () => {
    return (
      <header className={styles.heading}>
        <button onClick={back}>
          <h4>{`← Filter by ${title(filterName)}:`}</h4>
        </button>
        {filteredValues.length !== 0 && (
          <button className={styles.reset} onClick={clearValues}>
            Reset
          </button>
        )}
      </header>
    );
  };

  const WordLists = () => {
    return (
      <ul className={styles.list}>
        {filterLabels.map((value) => {
          return (
            <li className={styles.listItem} key={value.toLowerCase()}>
              <button className={styles.button} onClick={() => handleChange(value.toLowerCase())}>
                {filterName === TYPE ? trimType(value) : titleCase(value)}
                {updatedValues[value.toLowerCase()] && <CheckIcon />}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const SizeListSections = ({ list }) => {
    return (
      <ul className={styles.sizeList} key={list}>
        {sizeLists[list].map((value) => {
          const listItemStyles = `
            ${styles.sizeListItem}
            ${updatedValues[value.toLowerCase()] ? styles.selected : ""}
          `;

          if (sectionExists(value)) {
            return (
              <li className={listItemStyles} key={value.toLowerCase()}>
                <button
                  className={styles.sizeButton}
                  onClick={() => handleChange(value.toLowerCase())}
                >
                  {value.toUpperCase()}
                </button>
              </li>
            );
          }
        })}
      </ul>
    );
  };

  const SizeList = () => {
    return (
      <div className={styles.sizeContainer}>
        {Object.keys(sizeLists).map((list) => {
          if (sizeLists[list].length) {
            return (
              <div key={list} className={styles.listContainer}>
                <p>{titleCase(list)}</p>
                <SizeListSections list={list} />
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className={styles.container} ref={ref}>
      <Header />
      {filterName !== SIZE && <WordLists />}
      {filterName === SIZE && <SizeList />}
    </div>
  );
};

FilterListMobile.propTypes = {
  filterName: PropTypes.string,
  filterLabels: PropTypes.arrayOf(PropTypes.string),
  filteredValues: PropTypes.array,
  onChange: PropTypes.func,
  setSelectedFilter: PropTypes.func,
  setShowFilterMenu: PropTypes.func,
};
